/**
 * ActionCard — canonical card for a single action_item.
 *
 * Renders the shape emitted by GET /actions:
 *   {action_id, type, urgency, severity, title, description, due_at,
 *    cta_primary, cta_secondary, meta, ...}
 *
 * Mutations flow through `executeAction` with an `actionKey`; the card
 * is NOT optimistic — busy state while awaiting the backend 2xx, then
 * the parent's `reload()` refreshes the board. On failure the card
 * stays put and an inline error is shown; retry allowed.
 *
 * CTA visibility is role-aware via `resolvePrimaryCta`: when the CTA
 * is not authorised the primary button DOES NOT render (no disabled
 * state, no fallback redirect).
 *
 * The primary CTA is a REAL link (React Router <Link> for internal
 * hrefs): a plain click navigates as before, while right-click →
 * "open in new tab", middle-click and Cmd/Ctrl-click keep their native
 * browser behaviour — nothing intercepts navigation with onClick.
 */
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, DotsThreeVertical, WarningOctagon, Clock, Warning,
  CalendarBlank, Info, CheckCircle, Trash, Bell, EyeSlash,
} from "@phosphor-icons/react";
import { executeAction } from "@/lib/actionsApi";
import { ACTIONS_UPDATED_EVENT } from "@/hooks/useActionCounts";
import {
  resolvePrimaryCta,
  FOLLOWUP_SNOOZE_PRESETS,
  isoForSnoozePreset,
} from "@/lib/actionCta";
import {
  urgencyStyleFor,
  urgencyLabel,
  humanizeDueAt,
} from "@/components/actionCenter/urgencyStyle";
import FollowUpActionDetails from "@/components/actionCenter/FollowUpActionDetails";
import ActionDetails from "@/components/actionCenter/ActionDetails";
import { isFollowUpAction } from "@/lib/actionFilters";
import { menuSecondaries } from "@/lib/actionContext";
import { formatError } from "@/lib/api";

const ICONS_BY_KEY = {
  WarningOctagon,
  Clock,
  Warning,
  CalendarBlank,
  Info,
};

export default function ActionCard({
  item,
  role,
  onReload,
  onHighlight = false,
  lang = "es",
  compact = false,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const kebabRef = useRef(null);

  const style = urgencyStyleFor(item.urgency);
  const UrgencyIcon = ICONS_BY_KEY[style.iconKey] || Info;

  const primary = useMemo(
    () => resolvePrimaryCta(item, role),
    [item, role],
  );

  const dueLabel = humanizeDueAt(item.due_at, new Date(), lang);

  // Un seguimiento comercial programado se enseña con el detalle del
  // tablero de Seguimientos. La tarjeta virtual de "Responder a X" NO: es
  // otro trabajo y no trae las mutaciones que este bloque ofrece.
  const showFollowUpDetails = !compact && isFollowUpAction(item);

  // El bloque comercial ya trae su propio "Abrir lead". Cuando la acción
  // principal es exactamente eso, enseñarla otra vez debajo era pedir dos
  // veces lo mismo. Si el motor recomienda algo distinto —"Preparar
  // mensaje" con una plantilla— esa sí aporta y se queda.
  const duplicatesOpenLead = showFollowUpDetails && primary
    && primary.href === `/leads/${item.entity_id}`;

  // Todo lo demás —pagos, obra, documentos, tareas de IA, automatización— se
  // lee con la misma gramática, con el contexto que cada tipo sabe de sí
  // mismo. La tarjeta de "Responder a X" entra por aquí: no es un seguimiento
  // programado, pero tiene contexto que enseñar igual que el resto.
  const showGenericDetails = !compact && !showFollowUpDetails;
  const showPrimary = Boolean(primary) && !duplicatesOpenLead;
  const primaryCta = showPrimary ? (
    <PrimaryCtaLink
      href={primary.href}
      busy={busy}
      testId={`ac-card-${item.action_id}-primary`}
    >
      {primary.label}
      <ArrowRight size={12} />
    </PrimaryCtaLink>
  ) : null;

  const runAction = async (actionKey, payload) => {
    setError(null);
    setBusy(true);
    setMenuOpen(false);
    setSnoozeOpen(false);
    try {
      await executeAction(item, actionKey, payload);
      // No optimistic UI: the card stays until the parent reloads.
      // Notify the sidebar badge so its counts stay in sync without a
      // Context/Redux. Dispatched ONLY on 2xx — failed mutations leave
      // both card and badge untouched (see catch below).
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(ACTIONS_UPDATED_EVENT));
      }
      onReload && onReload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  // Build the secondary menu ONLY with actions the backend declared;
  // the frontend never invents mutations.
  // En el tablero completo las mutaciones que RESUELVEN la tarjeta suben a la
  // vista, y el menú se queda solo con las que la hacen desaparecer sin
  // resolverla (descartar, ocultar): un paso más lejos, donde no se pulsan
  // por inercia. En `compact` no hay sitio para botones, así que el menú las
  // conserva todas y nada se pierde.
  const declared = Array.isArray(item.cta_secondary) ? item.cta_secondary : [];
  const secondaries = compact ? declared : menuSecondaries(item);

  const openMenu = (e) => {
    e.stopPropagation();
    setMenuOpen((v) => !v);
    setSnoozeOpen(false);
  };

  const renderMenuItem = (secondary) => {
    const key = secondary.action_key;
    // Follow-up snooze needs a preset picker — expand inline.
    if (key === "followup_snooze") {
      return (
        <div key={key}>
          <button
            type="button"
            className="w-full text-left text-sm px-3 py-2 hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center gap-2"
            onClick={(e) => {
              e.stopPropagation();
              setSnoozeOpen((v) => !v);
            }}
            data-testid={`ac-card-${item.action_id}-snooze-open`}
          >
            <Clock size={14} />
            {lang === "es" ? "Posponer" : "Snooze"}
          </button>
          {snoozeOpen && (
            <div className="pl-6 pb-1">
              {FOLLOWUP_SNOOZE_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className="w-full text-left text-xs px-3 py-1.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800"
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    const until = isoForSnoozePreset(p.key);
                    if (until) runAction("followup_snooze", { until });
                  }}
                  data-testid={`ac-card-${item.action_id}-snooze-${p.key}`}
                >
                  {lang === "es" ? p.label : p.labelEn}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }
    // Everything else is a single-click button.
    const label = secondary.label
      || defaultLabelFor(key, lang);
    const Icon = iconFor(key);
    return (
      <button
        key={key}
        type="button"
        className="w-full text-left text-sm px-3 py-2 hover:bg-stone-100 dark:hover:bg-stone-800 flex items-center gap-2 disabled:opacity-50"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          runAction(key);
        }}
        data-testid={`ac-card-${item.action_id}-${key}`}
      >
        {Icon && <Icon size={14} />}
        {label}
      </button>
    );
  };

  return (
    <div
      id={`action-card-${item.action_id}`}
      className={`rounded-lg border border-l-4 transition-all ${style.bg} ${style.border} ${style.stripe} ${
        onHighlight ? "ring-2 ring-offset-2 ring-amber-400 dark:ring-offset-stone-950" : ""
      }`}
      data-testid={`ac-card-${item.action_id}`}
      data-urgency={item.urgency}
      data-type={item.type}
    >
      <div className="p-3">
        <div className="flex items-start gap-2">
          <div className={`shrink-0 mt-0.5 ${style.color}`}>
            <UrgencyIcon size={compact ? 14 : 16} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${style.badge}`}
                data-testid={`ac-card-${item.action_id}-urgency`}
              >
                {urgencyLabel(item.urgency, lang)}
              </span>
              {dueLabel && (
                <span className="text-[11px] text-stone-500 dark:text-stone-400">
                  {dueLabel}
                </span>
              )}
            </div>
            <div className={`mt-1 font-medium text-sm text-stone-900 dark:text-stone-100 ${compact ? "line-clamp-1" : ""}`}>
              {item.title || "—"}
            </div>
            {item.description && !compact && (
              <div className="mt-0.5 text-xs text-stone-600 dark:text-stone-400 line-clamp-2">
                {item.description}
              </div>
            )}
          </div>
          {secondaries.length > 0 && (
            <div className="relative" ref={kebabRef}>
              <button
                type="button"
                onClick={openMenu}
                className="p-1.5 rounded hover:bg-stone-100 dark:hover:bg-stone-800"
                aria-label={lang === "es" ? "Más opciones" : "More options"}
                data-testid={`ac-card-${item.action_id}-menu`}
              >
                <DotsThreeVertical size={16} />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 z-20 min-w-[180px] rounded-md border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-md py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {secondaries.map(renderMenuItem)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detalle comercial. Solo en el tablero completo: en `compact` la
            tarjeta vive en el dashboard y tiene que caber en una línea. */}
        {showFollowUpDetails && (
          <FollowUpActionDetails
            item={item}
            busy={busy}
            onRunAction={runAction}
            primaryCta={primaryCta}
          />
        )}
        {showGenericDetails && (
          <ActionDetails
            item={item}
            busy={busy}
            onRunAction={runAction}
            primaryCta={primaryCta}
          />
        )}

        {/* En `compact` no hay bloque de detalle, así que la CTA principal
            necesita su propia fila (y el alto reservado, que ahí iguala las
            tarjetas de la rejilla). En la lista la CTA viaja DENTRO de la
            fila de botones del detalle: dos filas de botones apiladas
            dejaban un hueco muerto y estiraban la tarjeta a lo alto. */}
        {(compact || busy) && (
          <div className={`mt-2 flex items-center gap-2 ${compact ? "min-h-[36px]" : "justify-end"}`}>
            {compact && primaryCta}
            {busy && (
              <span className="text-[11px] text-stone-500" data-testid={`ac-card-${item.action_id}-busy`}>
                {lang === "es" ? "Procesando…" : "Processing…"}
              </span>
            )}
          </div>
        )}

        {error && (
          <div
            className="mt-2 text-[11px] text-red-700 dark:text-red-300"
            data-testid={`ac-card-${item.action_id}-error`}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

const PRIMARY_CTA_CLASS =
  "inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90 min-h-[44px] sm:min-h-0";

// Absolute URLs (scheme present) are not router paths. The backend only
// emits internal hrefs today; this guard keeps a future absolute href
// from being resolved as a relative route.
function isAbsoluteUrl(href) {
  return /^[a-z][a-z0-9+.-]*:/i.test(href || "");
}

/**
 * Real link for the primary CTA. Same look and the same
 * data-testid / data-cta-href contract as the former <button>, but the
 * browser owns the navigation: React Router's <Link> only handles plain
 * left clicks and leaves modified/middle/right clicks native. While a
 * mutation is in flight the link is visually and pointer-disabled
 * (mirrors the old disabled state) without swallowing the href.
 */
function PrimaryCtaLink({ href, busy, testId, children }) {
  const className = `${PRIMARY_CTA_CLASS}${busy ? " opacity-50 pointer-events-none" : ""}`;
  const shared = {
    className,
    "aria-disabled": busy || undefined,
    tabIndex: busy ? -1 : undefined,
    "data-testid": testId,
    "data-cta-href": href,
  };
  if (isAbsoluteUrl(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...shared}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} {...shared}>
      {children}
    </Link>
  );
}

function iconFor(actionKey) {
  switch (actionKey) {
    case "followup_complete":
    case "task_complete":
    case "reply_acknowledge":
      return CheckCircle;
    case "followup_dismiss":
      return Trash;
    case "alert_dismiss":
      return EyeSlash;
    case "alert_snooze":
      return Bell;
    default:
      return null;
  }
}

function defaultLabelFor(actionKey, lang) {
  const es = {
    followup_complete: "Completar",
    followup_dismiss: "Descartar",
    task_complete: "Marcar hecha",
    alert_dismiss: "Ocultar",
    alert_snooze: "Posponer 7 días",
    reply_acknowledge: "Marcar como atendido",
  };
  const en = {
    followup_complete: "Complete",
    followup_dismiss: "Dismiss",
    task_complete: "Mark done",
    alert_dismiss: "Hide",
    alert_snooze: "Snooze 7 days",
    reply_acknowledge: "Mark as handled",
  };
  const map = lang === "en" ? en : es;
  return map[actionKey] || actionKey;
}
